/**
 * CreateApiKeyModal - form to create a new bsk_live_* API key.
 *
 * Fields:
 *  - Name (required).
 *  - Permission matrix: triple-state none / read / write for each of the
 *    resources (tickets, comments, attachments, users, webhooks,
 *    search, tasks, reminders, tags, replies). Only entries that are not `none`
 *    are sent to the backend.
 *  - `is_ai_agent` checkbox with explainer copy.
 *  - Optional expires_at (datetime-local input, converted to ISO).
 *  - Optional IP allowlist (textarea, one CIDR per line).
 *  - Optional description.
 *
 * On submit calls the supplied `onSubmit` mutation handler. The parent
 * owns the resulting raw_key reveal because that interaction extends
 * past this modal's lifetime.
 */
import { useState, type FormEvent } from "react";

import { Alert, Button, Input, Modal, Select, Textarea } from "@/components/ui";
import type {
  ApiKeyCreateRequest,
  ApiKeyPermissionLevel,
  ApiKeyResource,
} from "@/types/api";

const RESOURCES: ApiKeyResource[] = [
  "tickets",
  "comments",
  "attachments",
  "users",
  "webhooks",
  "search",
  // Tasks lists + reminders (2026-08-08). ``reminders`` is read-only
  // by design on the public API - a write grant has no endpoint to
  // use it on - but the matrix keeps the uniform vocabulary.
  "tasks",
  "reminders",
  // Internal ticket labels (2026-08-30). `write` lets a key tag the
  // tickets it creates or updates; `read` lets it see tags on a
  // ticket and filter by `?tag=`. Customers never see tags either
  // way - this grant only opens the API-key surface.
  "tags",
  // Autonomous reply sending (2026-09-26). `write` lets an AI key send
  // its own draft when the ticket requester's domain is on the AI
  // Autonomous Replies allowlist in Settings. Any other level = drafts
  // only, as before.
  "replies",
];

const LEVEL_OPTIONS: { value: ApiKeyPermissionLevel; label: string }[] = [
  { value: "none", label: "None" },
  { value: "read", label: "Read" },
  { value: "write", label: "Read + write" },
];

type PermissionState = Record<ApiKeyResource, ApiKeyPermissionLevel>;

function blankPermissions(): PermissionState {
  return {
    tickets: "none",
    comments: "none",
    attachments: "none",
    users: "none",
    webhooks: "none",
    search: "none",
    tasks: "none",
    reminders: "none",
    tags: "none",
    replies: "none",
  };
}

export interface CreateApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called with a fully-shaped ApiKeyCreateRequest. The parent runs the
   * mutation and surfaces the resulting raw_key.
   */
  onSubmit: (body: ApiKeyCreateRequest) => Promise<void> | void;
  isSubmitting?: boolean;
  /** Error message from the parent's mutation, if any. */
  error?: string | null;
}

export function CreateApiKeyModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  error,
}: CreateApiKeyModalProps): JSX.Element {
  const [name, setName] = useState("");
  const [permissions, setPermissions] = useState<PermissionState>(
    blankPermissions(),
  );
  const [isAiAgent, setIsAiAgent] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");
  const [ipAllowlist, setIpAllowlist] = useState("");
  const [description, setDescription] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const resetForm = () => {
    setName("");
    setPermissions(blankPermissions());
    setIsAiAgent(false);
    setExpiresAt("");
    setIpAllowlist("");
    setDescription("");
    setLocalError(null);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!name.trim()) {
      setLocalError("Name is required.");
      return;
    }

    // Build the permissions payload - drop `none` entries server-side
    // would also do, but the wire body stays tidy this way.
    const permissionsBody: Partial<
      Record<ApiKeyResource, ApiKeyPermissionLevel>
    > = {};
    for (const resource of RESOURCES) {
      const level = permissions[resource];
      if (level !== "none") {
        permissionsBody[resource] = level;
      }
    }

    const ipList = ipAllowlist
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const body: ApiKeyCreateRequest = {
      name: name.trim(),
      permissions: permissionsBody,
      is_ai_agent: isAiAgent,
      expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      ip_allowlist: ipList.length > 0 ? ipList : null,
      description: description.trim() || null,
    };

    try {
      await onSubmit(body);
      // Parent dismisses on success (the show-once modal opens in its
      // place). We still clear the form so that re-opening this dialog
      // is a clean slate.
      resetForm();
    } catch {
      // Parent surfaces the error via the `error` prop; do nothing here.
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create new API key"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {(error || localError) && (
          <Alert variant="error">{error ?? localError}</Alert>
        )}

        <Input
          id="api-key-name"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. AI draft agent"
          autoFocus
        />

        <div>
          <span className="block text-sm font-medium text-neutral-700">
            Permissions
          </span>
          <p className="mt-1 text-xs text-neutral-500">
            Pick the access level for each resource. Anything left at None is
            not sent on the wire.
          </p>
          <div className="mt-3 space-y-3">
            {RESOURCES.map((resource) => (
              <div
                key={resource}
                className="flex items-center justify-between gap-3"
              >
                <span className="text-sm font-medium capitalize text-neutral-800">
                  {resource}
                </span>
                <Select
                  id={`perm-${resource}`}
                  value={permissions[resource]}
                  onChange={(e) =>
                    setPermissions((prev) => ({
                      ...prev,
                      [resource]: e.target.value as ApiKeyPermissionLevel,
                    }))
                  }
                  options={LEVEL_OPTIONS}
                  className="w-44"
                  aria-label={`Permission level for ${resource}`}
                />
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={isAiAgent}
              onChange={(e) => setIsAiAgent(e.target.checked)}
              aria-label="AI agent"
              className="mt-1 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
            />
            <span>
              <span className="block text-sm font-medium text-neutral-800">
                AI agent
              </span>
              <span className="block text-xs text-neutral-500">
                AI-agent keys draft replies privately for a human to send.
                With replies set to Read + write they may also send their
                own drafts, but only for requester domains on the AI
                Autonomous Replies allowlist in Settings.
              </span>
            </span>
          </label>
        </div>

        <Input
          id="api-key-expires"
          label="Expires at (optional)"
          type="datetime-local"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          helperText="Leave blank for no expiry."
        />

        <Textarea
          id="api-key-ip-allowlist"
          label="IP allowlist (optional)"
          value={ipAllowlist}
          onChange={(e) => setIpAllowlist(e.target.value)}
          rows={3}
          placeholder="10.0.0.0/8&#10;192.168.1.1/32"
          helperText="One CIDR per line. Leave blank for no restriction."
        />

        <Textarea
          id="api-key-description"
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
        />

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting}>
            Create API key
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default CreateApiKeyModal;
